import { BannerSource, Ratio } from "@simplyk/common";
import { Path, UseFormReturn, FieldValues } from "react-hook-form";

import { ImageDimensions } from "@/common/types/image";

export type ImageSource = "upload" | "zeffy_library" | "unsplash" | "my_images";

export type ImageLibraryPresentation = "dialog" | "sheet";

/**
 * Maps frontend image selection sources to backend BannerSource enum values.
 * Used to track where banner images come from.
 */
export const mapImageSourceToBannerSource = (source: ImageSource): BannerSource => {
  switch (source) {
    case "upload":
    case "my_images":
      return BannerSource.CustomImage;
    case "zeffy_library":
      return BannerSource.ZeffyLibraryImage;
    case "unsplash":
      return BannerSource.UnsplashImage;
  }
};

/**
 * Context for authorizing image uploads for P2P/fundraiser campaigns and for persisting a banner's
 * AI-resolved alt text back to its form once captioning completes server-side.
 */
export type FundraiserContext = {
  /** The donation form ID - used for authorization and banner alt-text persistence */
  donationFormId?: string;
  /** The ticketing ID - banner alt-text persistence target for ticketing forms */
  ticketingId?: string;
  /** Optional token for unauthenticated fundraiser access */
  fundraiserToken?: string;
};

export enum ImageType {
  Logo = "logo",
  Banner = "banner",
  Signature = "signature",
  OrganizationLogo = "organizationLogo",
  ItemVisual = "itemVisual",
  TicketBanner = "ticketBanner",
  ProfilePhoto = "profilePhoto",
}

/**
 * Identifies the context/location where the ImageUploader is used.
 * Used for amplitude tracking to understand where image uploads occur.
 */
export enum ImageUploadContext {
  LiveFormEditorBanner = "live_form_editor_banner",
  LiveFormEditorLogo = "live_form_editor_logo",
  LiveFormEditorETicket = "live_form_editor_eticket",
  LiveFormEditorRateImage = "live_form_editor_rate_image",
  OrgPageEditorBanner = "org_page_editor_banner",
  OrgPageEditorLogo = "org_page_editor_logo",
  SettingsVisualIdentity = "settings_visual_identity",
  SettingsOrganizationInfo = "settings_organization_info",
  StripeOnboarding = "stripe_onboarding",
  PostMailLogo = "post_mail_logo",
  ManualCommandFundraising = "manual_command_fundraising",
  EmailBuilder = "email_builder",
}

export type ImageAltTextMetadata = {
  altText: string | null;
  /**
   * "pending" is emitted while AI captioning is still running server-side (the alt text will arrive in a
   * follow-up "ready" call)
   */
  status?: "pending" | "ready";
};

export interface ImageUploaderHandle {
  setImage: (url: string | null) => void;
  /**
   * Finalizes any pending square crop, uploads the final image, and returns the uploaded URL.
   * Returns null if no square editor is currently open.
   */
  finalizeSquareCrop: () => Promise<string | null>;
  openSelectImageModal: () => void;
  uploadFile: (file: File) => void;
}

export type ImageUploaderEmptyStateAction =
  | { type: "upload"; label?: string }
  | { type: "selectFromLibrary"; label?: string }
  | { type: "fillWithPlaceholder"; label?: string; onSelect: () => Promise<void> };

export type ImageUploaderEmptyState = { hidden: true } | { hidden?: false; actions: ImageUploaderEmptyStateAction[] };

export interface ImageUploaderLibrary {
  allowZeffyImages?: boolean;
  allowUnsplash?: boolean;
  defaultTab?: "myImages" | "zeffyImages";
  excludedZeffyImageTags?: string[];
  presentation?: ImageLibraryPresentation;
}

export type ImageEditorConfig =
  | {
      kind: "square";
      targetSize: number;
      centerFit?: boolean;
      whiteBackground?: boolean;
      previewSize?: number;
      /** Keep transparency: skip painting a white/blurred background behind the fitted image. */
      preserveTransparency?: boolean;
    }
  | {
      kind: "crop";
      maxDimensions?: ImageDimensions;
      showRatioPresets?: boolean;
      disableOnMobile?: boolean;
      disabled?: boolean;
      // The crop canvas keeps only the first frame, so GIFs skip it and are uploaded as-is.
      keepGifAnimation?: boolean;
    };

export interface ImageUploaderField<T extends FieldValues> {
  form: UseFormReturn<T>;
  name: Path<T>;
  isUploadingName: Path<T>;
  required?: boolean;
}

export interface ImageUploaderLayout {
  ratio: Ratio;
  maxHeight?: number;
  displayAspectRatio?: number;
}

export interface ImageUploaderUpload {
  context: ImageUploadContext;
  preset?: "default" | "logo-preset" | "square_800";
  amplitudePayload?: Record<string, unknown>;
  fundraiserContext?: FundraiserContext;
  persistToAssetLibrary?: boolean;
}

export interface ImageUploaderLabels {
  import: string;
  selectImageTitle?: string;
}

export interface ImageUploaderProps<T extends FieldValues> {
  field: ImageUploaderField<T>;
  editor: ImageEditorConfig;
  layout: ImageUploaderLayout;
  upload: ImageUploaderUpload;
  labels: ImageUploaderLabels;
  library?: ImageUploaderLibrary;
  emptyState?: ImageUploaderEmptyState;
  showDeleteButton?: boolean;
  disabled?: boolean;
  className?: string;
  "data-test"?: string;

  onImageSourceChange?: (source: BannerSource | null) => void;
  onImageAltTextMetadataChange?: (metadata: ImageAltTextMetadata | null) => void;
  onBeforeReplace?: () => boolean | Promise<boolean>;
  onBeforeDelete?: () => boolean | Promise<boolean>;
}
